import {
  ACTIVATE_URL,
  CREATE_USER_URLS,
  DEACTIVATE_URL,
  GET_USERS_AUDIT_LOGS,
} from '@/utils/constants/constants';
import { validate_phone_number } from '@/utils/validations/validate_inputs';
import axios from 'axios';
import Swal from 'sweetalert2';
import user_management_assignment_methods from './user_management_assignment_methods';

function parse_response_data(response_data) {
  return typeof response_data === 'string'
    ? JSON.parse(response_data)
    : response_data;
}

function get_create_user_identifier(created_user = {}) {
  return created_user.id || created_user.user_id || created_user.user?.id || null;
}

function build_toggle_user_request(user) {
  return {
    id: user.user_id,
    action: user.is_active ? 'deactivate' : 'activate',
    url: user.is_active ? DEACTIVATE_URL : ACTIVATE_URL,
  };
}

export default {
  set_log_modal_state(user_id) {
    this.selected_user_id = user_id;
    this.is_loading_logs = true;
    this.application_logs = [];
  },
  finalize_log_modal_state() {
    this.is_loading_logs = false;
    this.show_log_modal = true;
  },
  apply_user_logs_response(data) {
    this.application_logs = data.status === 'success'
      ? this.format_application_logs(data.data)
      : [];
    if (data.status !== 'success') {
      this.show_error(data.message || 'Failed to fetch user logs.');
    }
  },
  get_user_logs_request_params(row) {
    return row.user_id || row._rawApplicationData?.user_id || null;
  },
  format_application_logs(logs = []) {
    return logs.map(log => ({
      ...log,
      timestamp: this.format_date(log.timestamp),
    }));
  },
  async open_log_modal(row) {
    const user_id = this.get_user_logs_request_params(row);
    if (!user_id) return;
    this.set_log_modal_state(user_id);
    return axios.get(GET_USERS_AUDIT_LOGS, {
      headers: this.headers,
      params: { user_id },
    }).then(({ data }) => {
      this.apply_user_logs_response(data);
    }).catch((err) => {
      this.show_error(err.response?.data?.message || 'Unexpected error fetching logs.');
    }).finally(() => {
      this.finalize_log_modal_state();
    });
  },

  validate_phone_number(phone_number) {
    return validate_phone_number(phone_number);
  },

  add_user() {
    this.show_modal = true;
  },

  close_log_modal() {
    this.show_log_modal = false;
    this.selected_user_id = null;
  },
  is_create_user_phone_valid() {
    return !this.phone_number || this.validate_phone_number(this.phone_number);
  },
  build_create_user_payload() {
    return {
      first_name: this.first_name,
      last_name: this.last_name,
      email: this.email,
      phone_number: this.phone_number,
      role: this.role,
    };
  },
  reset_created_user_form() {
    this.first_name = '';
    this.last_name = '';
    this.phone_number = '';
    this.email = '';
    this.role = '';
  },
  parse_create_user_error_payload(response_data) {
    return typeof response_data === 'string'
      ? parse_response_data(response_data)
      : response_data;
  },
  flatten_create_user_error_data(data, fallback) {
    if (typeof data === 'string') return data;
    return data && typeof data === 'object'
      ? Object.values(data).flat().join(', ')
      : fallback;
  },
  parse_create_user_error(error) {
    const fallback = 'Error creating user, ensure all details are provided correctly.';
    const response_data = error.response?.data;
    if (!response_data) return fallback;
    const parsed = this.parse_create_user_error_payload(response_data);
    if (parsed.message) return parsed.message;
    return this.flatten_create_user_error_data(parsed.data, fallback);
  },
  async handle_created_user_assignment(created_user = {}) {
    this.user_id = get_create_user_identifier(created_user);
    const project_id = Number(this.project_id);
    const has_valid_project = Number.isFinite(project_id) && project_id > 0;
    if (!has_valid_project || !this.user_id) return;
    await this.toggle_assignment(project_id, true);
  },
  finalize_created_user_submission() {
    this.show_success('User has been successfully created.');
    this.close_modal('create_user');
    this.reset_created_user_form();
    return this.fetch_users();
  },

  async submit_user() {
    if (!this.is_create_user_phone_valid()) {
      this.show_info('Please enter a valid 10-digit phone number.');
      return;
    }
    const payload = this.build_create_user_payload();
    return axios.post(CREATE_USER_URLS, payload, {
      headers: this.headers,
    }).then(async (response) => {
      if (response.data.status !== 'success') return;
      await this.handle_created_user_assignment(response.data.data || {});
      await this.finalize_created_user_submission();
    }).catch((error) => {
      this.show_error(this.parse_create_user_error(error));
    });
  },

  edit_user(user) {
    const name_parts = user.name.split(' ');
    const matching_role = this.user_roles.find(r => r.role === user.role);
    this.edit_user_data = {
      user_id: user.user_id,
      first_name: name_parts[0],
      last_name: name_parts[1] || '',
      email: user.email,
      phone_number: user.phone_number || '',
      role: user.role,
      role_id: matching_role ? matching_role.id : null,
    };
  },

  apply_toggle_user_status_success(user, action) {
    this.show_success(`User ${action}d successfully.`);
    user.is_active = !user.is_active;
    this.fetch_users();
  },
  async toggle_user_status(user) {
    const request = build_toggle_user_request(user);
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `This will ${request.action} the user.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${request.action}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    return axios.post(request.url, { user_id: request.id }, { headers: this.headers })
      .then(({ data }) => {
        if (data.status !== 'success') {
          this.show_error(data.message || 'An error occurred.');
          return;
        }
        this.apply_toggle_user_status_success(user, request.action);
      })
      .catch((err) => {
        this.show_error(err.response?.data?.message || err.message || 'Unexpected error');
      });
  },

  ...user_management_assignment_methods,
};
